import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import SearchIcon from '@mui/icons-material/Search'
import { query } from '@cbgms/components/list'
import { searchUser } from '../state/actions'
import { ICompany } from '@cbgms/api/modules/company'
import { isValidEmail } from '@cbgms/base/utils/validations'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { useSafeState } from '@cbgms/base/hooks/use-safe-state'
import { MenuItem } from 'core/page/MenuItem'
import { useNotifier } from '@cbgms/base/notifier'

const isSearchEmailValid = (probablyValidEmail: string) => !isValidEmail(probablyValidEmail)

interface SearchByCognitoEmailProps {
  setAPISearchQuery: (newSearchQuery: string) => void
  setHasUsersWithoutCompany: (b: boolean) => void
}

interface CompanyUUIDsSearchQuery {
  Company: ICompany
}

export const SearchByCognitoEmail: React.FC<SearchByCognitoEmailProps> = ({ setAPISearchQuery, setHasUsersWithoutCompany }) => {
  const [emailInputValue, setEmailInputValue] = useSafeState<string>('')

  const dispatch = useDispatch()

  const { t } = useTranslation()
  const notifier = useNotifier()
  const searchEmailValid = isSearchEmailValid(emailInputValue)

  const getCompanyUUIDsSearchQueryByCognitoEmail = React.useCallback(async (maybeCognitoEmail: string): Promise<string> => {
    const companyUUIDsSearchQuery = query<CompanyUUIDsSearchQuery>()

    const { Data: usersFoundByCognitoEmail } = await dispatch(
      searchUser({
        EmailAddress: maybeCognitoEmail
      })
    )

    const usersInCompany = usersFoundByCognitoEmail?.filter(user => !!user.CompanyUUID)

    if (usersInCompany?.length) {
      companyUUIDsSearchQuery.groupStart()
      usersInCompany.forEach((user, index) => {
        companyUUIDsSearchQuery.equals(c => c.Company.UUID, user.CompanyUUID)
        if (index < usersFoundByCognitoEmail.length - 1) companyUUIDsSearchQuery.or()
      })
      companyUUIDsSearchQuery.groupEnd()
    } else {
      // no company uuids found - let's use an empty uuid in this case
      companyUUIDsSearchQuery.equals(c => c.Company.UUID, EmptyUUID)
    }

    setHasUsersWithoutCompany(!!usersFoundByCognitoEmail?.find(user => !user.CompanyUUID))
    return companyUUIDsSearchQuery.toString()
  }, [])

  const updateSearchQuery = React.useCallback(
    async (maybeValidCognitoEmail: string) => {
      if (!maybeValidCognitoEmail) {
        setAPISearchQuery('')
        return
      }

      if (isSearchEmailValid(maybeValidCognitoEmail)) {
        try {
          const companyUUIDsSearchQuery = await getCompanyUUIDsSearchQueryByCognitoEmail(maybeValidCognitoEmail)

          setAPISearchQuery(companyUUIDsSearchQuery)
        } catch (e: any) {
          notifier.error(e.message)
        }
      }
    },
    [setAPISearchQuery]
  )

  const emailInputOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const maybeCognitoEmail = event.target.value.slice(0, 255)
    setEmailInputValue(maybeCognitoEmail)
    if (!maybeCognitoEmail) {
      updateSearchQuery('')
    }
  }, [])

  const emailInputOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault()

      // search on Enter
      updateSearchQuery(emailInputValue)
    }
  }

  return (
    <Grid item display='flex' alignItems='center' gap={1}>
      <TextField
        size='small'
        sx={{ minWidth: 450 }}
        variant='outlined'
        placeholder={t('grid.search_by_cognito_email')}
        type='text'
        error={!searchEmailValid && !!emailInputValue}
        onChange={emailInputOnChange}
        onKeyDown={emailInputOnKeyDown}
        value={emailInputValue}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {emailInputValue && (
                <IconButton
                  onClick={() => {
                    setEmailInputValue('')
                    updateSearchQuery('')
                  }}
                  size='large'
                >
                  <HighlightOffIcon />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
      />
      <Button
        startIcon={<SearchIcon />}
        variant='contained'
        disableElevation
        onClick={() => updateSearchQuery(emailInputValue)}
        disabled={!searchEmailValid || !emailInputValue}
      >
        {t('global.search')}
      </Button>
    </Grid>
  )
}

interface UseSearchByCognitoEmail {
  getSearchComponent: () => JSX.Element
  apiSearchQuery: string
  hasUsersWithoutCompany: boolean
}

export const useSearchByCognitoEmail = (): UseSearchByCognitoEmail => {
  const [apiSearchQuery, setAPISearchQuery] = useSafeState<string>('')
  const [hasUsersWithoutCompany, setHasUsersWithoutCompany] = useSafeState<boolean>(false)

  return {
    getSearchComponent: () => (
      <MenuItem>
        <SearchByCognitoEmail setAPISearchQuery={setAPISearchQuery} setHasUsersWithoutCompany={setHasUsersWithoutCompany} />
      </MenuItem>
    ),
    apiSearchQuery,
    hasUsersWithoutCompany
  }
}
